import convertRGBtoHSL from './convertRGBtoHSL';
import convertHSLtoRGB from './convertHSLtoRGB';
import getContrast from './getContrast';
import { RGB } from './types';

/*
  This utility takes two colors as arguments and darkens the primaryRGB value until
  it meets a W3C acceptable contrast ratio.
*/

const darkenRGBforAccessibleContrast = (
  /*
   * Color to darken
   */
  primaryRGB: RGB,

  /*
   * Color to check contrast against
   */
  secondaryRGB: RGB,

  /*
   * Targeted contrast level
   * see: https://www.w3.org/TR/2008/REC-WCAG20-20081211/#contrast-ratiodef
   */
  contrastTarget: number = 4.5,

  /*
   * PRIVATE: used for function recursion
   */
  _contrastDenominator: number = 1
): { rgb: { r: number; g: number; b: number }; contrast: number } => {
  const { h, s, l } = convertRGBtoHSL(primaryRGB);
  const { r, g, b } = convertHSLtoRGB({ h, s, l: l / _contrastDenominator });

  const contrast = getContrast(secondaryRGB, { r, g, b });

  if (!contrast) {
    throw Error('Contrast is undefined. Make sure that arguments are defined.');
  }

  if (contrast >= contrastTarget) {
    return { rgb: primaryRGB, contrast };
  }

  if (primaryRGB.r === 0 && primaryRGB.g === 0 && primaryRGB.b === 0) {
    throw Error(
      'Color was darkened to black and accessible contrast was not achieved. Try lightening your secondaryRGB value.'
    );
  }

  const inc = (contrastTarget - contrast) / 100;
  return darkenRGBforAccessibleContrast(
    { r, g, b },
    secondaryRGB,
    contrastTarget,
    _contrastDenominator + inc
  );
};

export default darkenRGBforAccessibleContrast;
